import React, { useEffect, useRef } from "react";
import { LogOut, X, AlertTriangle, Loader2 } from "lucide-react";

/**
 * Reusable Sign Out Confirmation Dialog to prevent accidental logouts.
 * Works seamlessly in both the tenant application and Super Admin Mission Control.
 *
 * @param {boolean} isOpen - Controls dialog visibility
 * @param {boolean} open - Alias for isOpen
 * @param {() => void} onClose - Invoked when user cancels or dismisses the dialog
 * @param {() => void | Promise<void>} onConfirm - Invoked when user confirms sign-out
 * @param {string} [userName] - Username of the current user
 * @param {string} [accountName] - Company or Portfolio name
 * @param {string} [role] - User role (e.g. "owner", "editor", "viewer", "superadmin")
 * @param {boolean} [isSuperAdmin] - Whether this is invoked from Super Admin console
 * @param {boolean} [signingOut] - Loading state while signout request is in progress
 */
export default function SignOutConfirmModal({
  isOpen,
  open,
  onClose,
  onConfirm,
  userName = "User",
  accountName,
  role,
  isSuperAdmin = false,
  signingOut = false,
}) {
  const visible = isOpen ?? open ?? false;
  const cancelButtonRef = useRef(null);

  // Focus cancel button on open to prevent accidental Enter-key submissions
  useEffect(() => {
    if (visible) {
      document.body.classList.add("has-modal-open");
      const timer = setTimeout(() => {
        cancelButtonRef.current?.focus();
      }, 50);
      return () => {
        clearTimeout(timer);
        document.body.classList.remove("has-modal-open");
      };
    } else {
      document.body.classList.remove("has-modal-open");
    }
  }, [visible]);

  // Handle escape key
  useEffect(() => {
    if (!visible) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !signingOut) {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [visible, signingOut, onClose]);

  if (!visible) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !signingOut) {
      onClose?.();
    }
  };

  const initial = (userName || "U").charAt(0).toUpperCase();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-md animate-in fade-in duration-150"
      onClick={handleBackdropClick}
      data-testid="signout-confirm-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signout-modal-title"
    >
      <div
        className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border animate-in zoom-in-95 duration-150 transition-all ${
          isSuperAdmin
            ? "bg-[#0E1526] border-slate-800 text-slate-100"
            : "bg-[var(--surface)] border-[var(--border)] text-[var(--text)]"
        }`}
        onClick={(e) => e.stopPropagation()}
        data-testid="signout-confirm-modal"
      >
        {/* Header */}
        <div
          className={`px-6 py-5 border-b flex items-center justify-between gap-3 ${
            isSuperAdmin
              ? "border-slate-800/80 bg-slate-900/60"
              : "border-[var(--border)] bg-gradient-to-r from-[var(--surface)] to-[var(--surface-hover)]"
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-500/15 text-rose-500 border border-rose-500/25 flex items-center justify-center shrink-0">
              <LogOut className="w-5 h-5" />
            </div>
            <div>
              <h2
                id="signout-modal-title"
                className={`text-base font-bold tracking-tight ${
                  isSuperAdmin ? "text-white" : "text-[var(--text)]"
                }`}
              >
                {isSuperAdmin ? "Sign Out of Super Admin?" : "Sign Out of Rental Tracker?"}
              </h2>
              <p
                className={`text-xs mt-0.5 ${
                  isSuperAdmin ? "text-slate-400" : "text-[var(--muted)]"
                }`}
              >
                Confirm ending your active session
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={signingOut}
            data-testid="signout-cancel-x-button"
            className={`w-8 h-8 rounded-xl border flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isSuperAdmin
                ? "border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800"
                : "border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)]"
            }`}
            aria-label="Close dialog"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 space-y-4">
          {/* User & Company Identity Card */}
          <div
            className={`p-3.5 rounded-xl border flex items-center gap-3 ${
              isSuperAdmin
                ? "bg-slate-900/80 border-slate-800"
                : "bg-[var(--surface-hover)]/60 border-[var(--border)]"
            }`}
          >
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm shrink-0 shadow-sm ${
                isSuperAdmin
                  ? "bg-gradient-to-tr from-teal-500 to-emerald-400 text-slate-950"
                  : "bg-gradient-to-br from-teal-500 via-teal-600 to-[#1C3A5E] text-white"
              }`}
            >
              {initial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`text-sm font-semibold truncate ${
                    isSuperAdmin ? "text-white" : "text-[var(--text)]"
                  }`}
                >
                  {userName}
                </span>
                {isSuperAdmin ? (
                  <span className="px-1.5 py-0.2 rounded text-[9px] font-black uppercase tracking-wider bg-teal-500/15 text-teal-300 border border-teal-500/30">
                    Super Admin
                  </span>
                ) : role ? (
                  <span className="px-1.5 py-0.2 rounded text-[9px] font-black uppercase tracking-wider bg-teal-500/15 text-teal-400 border border-teal-500/30">
                    {role}
                  </span>
                ) : null}
              </div>
              <p
                className={`text-xs truncate mt-0.5 ${
                  isSuperAdmin ? "text-slate-400" : "text-[var(--muted)]"
                }`}
              >
                {accountName ? `${accountName} • ` : ""}Active Session
              </p>
            </div>
          </div>

          {/* Warning Notice */}
          <div
            className={`p-3 rounded-xl border flex items-start gap-2.5 ${
              isSuperAdmin
                ? "bg-amber-500/10 border-amber-500/25 text-amber-300"
                : "bg-amber-500/10 border-amber-500/25 text-amber-600 dark:text-amber-400"
            }`}
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="text-xs leading-relaxed">
              <span className="font-semibold">Unsaved work notice:</span> Any unsubmitted form
              entries or draft transactions will be discarded. You will need to sign in again to
              resume.
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div
          className={`px-6 py-4 border-t flex items-center justify-end gap-3 ${
            isSuperAdmin
              ? "border-slate-800/80 bg-slate-900/40"
              : "border-[var(--border)] bg-[var(--surface-hover)]/30"
          }`}
        >
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onClose}
            disabled={signingOut}
            data-testid="signout-cancel-button"
            className={`px-4 py-2.5 rounded-xl text-xs font-semibold border transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
              isSuperAdmin
                ? "border-slate-700/80 bg-slate-800 hover:bg-slate-700 text-slate-200"
                : "border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-hover)] text-[var(--text)]"
            }`}
          >
            Stay Signed In
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={signingOut}
            data-testid="signout-confirm-button"
            className="px-4 py-2.5 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-500 active:bg-rose-700 border border-rose-500/50 shadow-md shadow-rose-600/20 flex items-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]"
          >
            {signingOut ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Signing out...</span>
              </>
            ) : (
              <>
                <LogOut className="w-3.5 h-3.5" />
                <span>Yes, Sign Out</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
